import React, { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { logger } from '../utils/logger';
import { COLLECTIONS } from '../config/constants';

/**
 * ConnectionStatus Component
 * Shows Firebase connection status with visual indicator
 * Monitors both network connectivity and Firestore connection
 */
export const ConnectionStatus: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isFirestoreConnected, setIsFirestoreConnected] = useState(true);
  const [showStatus, setShowStatus] = useState(false);

  useEffect(() => {
    // Monitor browser network status
    const handleOnline = () => {
      setIsOnline(true);
      setShowStatus(true);
      setTimeout(() => setShowStatus(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowStatus(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Monitor Firestore connection with a minimal listener.
    // Tek bir public doc'u (config/marketingSettings) dinleyerek bağlantı kontrolü.
    const unsubscribe = onSnapshot(
      doc(db, COLLECTIONS.CONFIG, 'marketingSettings'),
      () => {
        // Successfully connected to Firestore
        if (!isFirestoreConnected) {
          setIsFirestoreConnected(true);
          setShowStatus(true);
          setTimeout(() => setShowStatus(false), 3000);
        }
      },
      (error) => {
        // Firestore connection error
        logger.error('Firestore connection error:', error);
        setIsFirestoreConnected(false);
        setShowStatus(true);
      }
    );

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, [isFirestoreConnected]);

  // Don't show anything if everything is fine and user doesn't need to know
  if (!showStatus && isOnline && isFirestoreConnected) {
    return null;
  }

  const getStatusConfig = () => {
    if (!isOnline) {
      return {
        text: '⚠️ İnternet bağlantısı yok',
        color: 'bg-red-500',
        icon: '🔴',
      };
    }
    if (!isFirestoreConnected) {
      return {
        text: '⚠️ Veritabanı bağlantısı koptu',
        color: 'bg-orange-500',
        icon: '🟠',
      };
    }
    return {
      text: '✅ Bağlantı başarılı',
      color: 'bg-green-500',
      icon: '🟢',
    };
  };

  const status = getStatusConfig();

  return (
    <div
      className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 ${status.color} text-white px-6 py-3 rounded-full shadow-lg z-50 flex items-center gap-3 animate-fade-in`}
      style={{
        animation: 'fadeIn 0.3s ease-in-out',
      }}
    >
      <span className="text-lg">{status.icon}</span>
      <span className="font-medium">{status.text}</span>
      {!isOnline || !isFirestoreConnected ? (
        <button
          onClick={() => window.location.reload()}
          className="ml-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-sm transition-colors"
        >
          Yenile
        </button>
      ) : null}
    </div>
  );
};

export default ConnectionStatus;
